import {
  advanceDiagnosticReportsCursor,
  usePatientDiagnosticReportsPage,
} from '@ctw/shared/api/fhir/diagnostic-report';
import type { DiagnosticReportModel } from '@ctw/shared/api/fhir/models/diagnostic-report';
import { withErrorBoundary } from '@ctw/shared/components/errors/error-boundary';
import { useMainContentHeader } from '@ctw/shared/components/layout/main-content-with-dockable-drawer';
import {
  PageSizeSelect,
  parsePageSizePreference,
} from '@ctw/shared/components/pagination/page-size-select';
import { Pagination } from '@ctw/shared/components/pagination/pagination';
import { SimplePagination } from '@ctw/shared/components/pagination/simple-pagination';
import { useDiagnosticRowActions } from '@ctw/shared/content/diagnostic-reports/helpers/row-actions';
import { EmptyPatientTable } from '@ctw/shared/content/empty-patients-table';
import { HotloadGate } from '@ctw/shared/content/hotload-gate';
import { ResourceTable } from '@ctw/shared/content/resource/resource-table';
import type { ResourceTableSortOption } from '@ctw/shared/content/resource/resource-table-actions';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { usePatientContext } from '@ctw/shared/context/patient-provider';
import { useResourceDrawer } from '@ctw/shared/context/resource-drawer-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { useWritebacks } from '@ctw/shared/context/writeback-provider';
import { useUiPreference } from '@ctw/shared/hooks/use-ui-preference';
import { getDefaultsFromLocalStorage, saveToLocalStorage } from '@ctw/shared/utils/local-storage';
import { tw } from '@ctw/shared/utils/tailwind';
import { useEffect, useRef, useState } from 'react';
import { ResourceTableActions } from '../resource/resource-table-actions';
import { patientDiagnosticReportsColumns } from './helpers/columns';
import {
  defaultDiagnosticReportSort,
  diagnosticReportSortOptions,
  diagnosticReportSortToFqsSortParams,
} from './helpers/sorts';

const DIAGNOSTIC_REPORTS_PAGE_SIZE = 25;

// Resolves the persisted sort selection to a canonical option so identity checks and the sort
// menu stay consistent (localStorage strips functions/predicates from the cached value).
const resolveInitialSort = (): ResourceTableSortOption<DiagnosticReportModel> => {
  const { defaultSort } = getDefaultsFromLocalStorage('patient-diagnostic-reports', {
    defaultSort: defaultDiagnosticReportSort,
  });
  return (
    diagnosticReportSortOptions.find((option) => option.display === defaultSort.display) ??
    defaultDiagnosticReportSort
  );
};

const PatientDiagnosticReportsServerSide = () => {
  const { patient } = usePatientContext();

  return (
    <HotloadGate upid={patient.UPID} resourceTypes={['DiagnosticReport']}>
      <PatientDiagnosticReportsContent />
    </HotloadGate>
  );
};

const PatientDiagnosticReportsContent = () => {
  const { requestContext, graphqlClient } = useCTW();
  const telemetry = useTelemetry();
  const { patient } = usePatientContext();
  const rowActions = useDiagnosticRowActions();
  const { openResourceDrawer } = useResourceDrawer({ resourceActions: rowActions as never });
  const { writebackStatuses } = useWritebacks();

  const [currentSort, setCurrentSort] =
    useState<ResourceTableSortOption<DiagnosticReportModel>>(resolveInitialSort);
  const [currentPage, setCurrentPage] = useState(1);
  // cursorsByPageIndex[k] is the cursor that fetches page k + 1; page 1 uses the empty cursor.
  const [cursorsByPageIndex, setCursorsByPageIndex] = useState<Record<number, string>>({ 0: '' });
  const [isJumping, setIsJumping] = useState(false);
  // Hold the last non-null total so the pager doesn't flicker between page transitions.
  const [lastKnownTotal, setLastKnownTotal] = useState<number | null>(null);
  // Bumped by every pagination reset (sort/size change) and by the start of every far-jump cursor
  // advance. A far jump's `.then`/`.catch`/`.finally` capture this value when the jump starts and
  // recheck it when the jump settles, so a resolution that lands after a newer reset or a newer
  // jump has superseded it applies no state instead of overwriting the current page with stale
  // cursors or clobbering a still-in-flight jump's own spinner.
  const requestGenerationRef = useRef(0);

  const [storedPageSize, setStoredPageSize] = useUiPreference(
    'diagnosticReportsPageSize',
    String(DIAGNOSTIC_REPORTS_PAGE_SIZE),
  );
  const pageSize = parsePageSizePreference(storedPageSize, DIAGNOSTIC_REPORTS_PAGE_SIZE);

  const query = usePatientDiagnosticReportsPage(
    pageSize,
    cursorsByPageIndex[currentPage - 1] ?? '',
    diagnosticReportSortToFqsSortParams(currentSort),
  );

  // Store the fetched page's endCursor so Prev/Next can revisit adjacent pages in one request.
  useEffect(() => {
    const endCursor = query.data?.pageInfo.endCursor;
    if (endCursor === undefined) {
      return;
    }
    setCursorsByPageIndex((prev) =>
      prev[currentPage] === endCursor ? prev : { ...prev, [currentPage]: endCursor },
    );
  }, [query.data, currentPage]);

  useEffect(() => {
    const total = query.data?.pageInfo.total;
    if (typeof total === 'number') {
      setLastKnownTotal(total);
    }
  }, [query.data]);

  const handleSortChange = (option: ResourceTableSortOption<DiagnosticReportModel>) => {
    setCurrentSort(option);
    saveToLocalStorage('patient-diagnostic-reports', { defaultSort: option });
    // FQS rejects a cursor minted under a different sort, so reset pagination on every change.
    requestGenerationRef.current += 1;
    setCursorsByPageIndex({ 0: '' });
    setCurrentPage(1);
    // A far jump in flight was advancing cursors under the old sort; the generation bump above
    // discards its resolution, so nothing else would otherwise clear its spinner.
    setIsJumping(false);
  };

  const handlePageSizeChange = (nextPageSize: number) => {
    setStoredPageSize(String(nextPageSize));
    // A cached cursor only fetches the correct window at the page size it was captured under, so
    // a size change must clear the cache the same way a sort change does.
    requestGenerationRef.current += 1;
    setCursorsByPageIndex({ 0: '' });
    setCurrentPage(1);
    // A far jump in flight was advancing cursors under the old page size; the generation bump
    // above discards its resolution, so nothing else would otherwise clear its spinner.
    setIsJumping(false);
  };

  const handlePageChange = (target: number) => {
    if (target === currentPage) {
      return;
    }
    if (cursorsByPageIndex[target - 1] !== undefined) {
      // Prev/Next and any previously visited page: fetch directly from the known cursor.
      setCurrentPage(target);
      return;
    }

    // Far jump to an unseen page: advance from the nearest known cursor before the target.
    const knownIndexes = Object.keys(cursorsByPageIndex)
      .map(Number)
      .filter((index) => index < target - 1);
    const fromPageIndex = Math.max(0, ...knownIndexes);
    const count = (target - 1 - fromPageIndex) * pageSize;

    setIsJumping(true);
    // Bump and capture this jump's own generation: a later reset or a newer far jump bumps it
    // again, and the checks below then know this jump's resolution has been superseded.
    requestGenerationRef.current += 1;
    const requestGeneration = requestGenerationRef.current;
    advanceDiagnosticReportsCursor(
      telemetry,
      graphqlClient,
      patient,
      {
        cursor: cursorsByPageIndex[fromPageIndex] ?? '',
        fromPageIndex,
        pageSize,
        count,
        sort: diagnosticReportSortToFqsSortParams(currentSort),
      },
      { requestHeaders: { 'X-Use-Hotstorage': '1', 'X-Canonical-Only': '1' } },
    )
      .then(({ cursorsByPageIndex: discovered, exhausted }) => {
        // A reset or a newer far jump started since this one began: its cursors were minted under
        // conditions (sort, page size, or a different target) that no longer apply, or a newer
        // jump already owns the current page — either way, applying them now would be wrong.
        if (requestGenerationRef.current !== requestGeneration) {
          return;
        }
        // Functional update: a page visited while this jump was in flight (e.g. Prev/Next to an
        // already-known page) can have added its own endCursor since the jump started. Merging
        // into the latest state, rather than the state captured when the jump began, keeps that
        // entry instead of overwriting it with a stale pre-jump value.
        setCursorsByPageIndex((prev) => ({ ...prev, ...discovered }));
        // If the connection ran out mid-jump, clamp to the last page this jump actually reached.
        // Basing that on `discovered` — this skip's own page-aligned boundaries — means a
        // connection that shrank since an earlier page was visited can't resurrect a now-stale
        // cursor beyond where this skip ran out. Falling back further to `fromPageIndex + 1`
        // covers the jump exhausting before confirming even one full page — that's the page the
        // jump started from, already known reachable before this call.
        const reachedIndexes = Object.keys(discovered).map(Number);
        const furthestReachedPage =
          reachedIndexes.length > 0 ? Math.max(...reachedIndexes) + 1 : fromPageIndex + 1;
        const reachable = exhausted ? furthestReachedPage : target;
        setCurrentPage(reachable);
      })
      .catch((error) => {
        // A superseded request (reset or newer jump) is stale; skip the log too so a jump the
        // user has already abandoned doesn't get reported as a failure.
        if (requestGenerationRef.current !== requestGeneration) {
          return;
        }
        // Leave the current page unchanged on failure; the table stops its jump spinner below.
        telemetry.logger.error('Failed to advance diagnostic reports cursor', { error });
      })
      .finally(() => {
        // A superseded reset already cleared the spinner synchronously above; a superseded newer
        // jump owns clearing it when that jump itself settles. Either way, this stale settlement
        // must not touch it.
        if (requestGenerationRef.current !== requestGeneration) {
          return;
        }
        setIsJumping(false);
      });
  };

  useMainContentHeader(
    'PatientDiagnosticReportsContentHeader',
    () => (
      <ResourceTableActions
        sortOptions={{
          defaultSort: currentSort,
          options: diagnosticReportSortOptions,
          onChange: handleSortChange,
        }}
      />
    ),
    [currentSort],
  );

  const total = lastKnownTotal;

  return (
    <div>
      <ResourceTable
        title="Diagnostic Reports"
        titleVisuallyHidden={true}
        showTableHead={true}
        isLoading={query.isLoading || isJumping}
        data={query.data?.reports ?? []}
        stackedBreakpoint="xs"
        columns={patientDiagnosticReportsColumns(requestContext.builderId, writebackStatuses)}
        onRowClick={({ telemetry: rowTelemetry, record }) =>
          openResourceDrawer({
            telemetry: rowTelemetry,
            model: record,
          })
        }
        rowActions={rowActions}
        enableReadActions={true}
        hidePagination={true}
        emptyMessage={
          <EmptyPatientTable hasZeroFilteredRecords={false} resourceName="diagnostics" />
        }
      />
      <div className={tw`flex flex-wrap items-center gap-2`}>
        {/* No min-w-0: this block keeps its natural minimum width, so the row wraps the page-size
            selector below once space is genuinely tight, rather than squeezing this content past
            its own readable size. */}
        <div className={tw`flex-1`}>
          {typeof total === 'number' ? (
            <Pagination
              currentPage={currentPage}
              setCurrentPage={handlePageChange}
              pageSize={pageSize}
              total={total}
            />
          ) : (
            <SimplePagination
              currentPage={currentPage}
              setCurrentPage={handlePageChange}
              hasNext={Boolean(query.data?.pageInfo.hasNextPage)}
            />
          )}
        </div>
        <PageSizeSelect
          label="Results per page"
          value={pageSize}
          onChange={handlePageSizeChange}
          className={tw`px-6 py-3`}
        />
      </div>
    </div>
  );
};

export const PatientDiagnosticReports = withErrorBoundary({
  boundaryName: 'PatientDiagnosticReports',
  Component: PatientDiagnosticReportsServerSide,
});
